<script setup lang="ts">
import type { AnimationItem } from 'lottie-web'

const _props = defineProps<{
  data: FullWidthMediaProps
  isOverlay: boolean
  sectionIndex: number
  template?: 'page' | 'overlay'
}>()

const videoProps = computed(() => ({
  controls: _props.data.media?.video && _props.data.showVideoControls,
  autoplay: !_props.data.media?.video || !_props.data.showVideoControls,
  muted: !_props.data.media?.video || !_props.data.showVideoControls,
  loop: !_props.data.media?.video || !_props.data.showVideoControls,
}))

const $el = useTemplateRef('$el')
const $animation = useTemplateRef('$animation')
const lottieInstance = ref<AnimationItem | null>(null)
const { $ScrollTrigger } = useNuxtApp()
async function lottieAnimation() {
  return new Promise(async (resolve, _reject) => {
    if ($animation.value) {
      const Lottie = (await import('lottie-web')).default

      $animation.value.style.transition = 'opacity 200ms'
      $animation.value.style.opacity = '0'

      lottieInstance.value = Lottie.loadAnimation({
        loop: false,
        container: $animation.value,
        path: '/lottie/love-from-nitex.json',
        autoplay: false,
      })

      lottieInstance.value.addEventListener('DOMLoaded', () => {
        if ($animation.value) {
          $animation.value.style.opacity = '1'
        }

        lottieInstance.value?.goToAndPlay(28)
        lottieInstance.value?.setSpeed(1.3)
      })
      lottieInstance.value.addEventListener('complete', resolve)
    }
  })
}

const createdTriggers: ScrollTrigger[] = []
async function setupScrollTriggers() {
  // Clear any existing triggers to avoid duplication
  createdTriggers.forEach((t) => t.kill())
  createdTriggers.length = 0
  const trigger = $ScrollTrigger.create({
    id: 'lottie-animation',
    trigger: $el.value,
    scroller: document.querySelector('#overlay') ?? null,
    start: 'top center',
    end: 'bottom center',
    // markers: true,
    once: true,
    onEnter: () => {
      lottieAnimation()
    },
  })

  createdTriggers.push(trigger)
}

onMounted(async () => {
  await nextTick()
  await cleanupAnimation()
  setupScrollTriggers()
})

async function cleanupAnimation(): Promise<void> {
  $ScrollTrigger.getById('lottie-animation')?.kill()
  createdTriggers.forEach((t) => t.kill())
  createdTriggers.length = 0
  await Promise.resolve()
}

onBeforeUnmount(async () => {
  await cleanupAnimation()
})
</script>

<template>
  <section
    ref="$el"
    class="relative"
    :class="[
      {
        'px-bleed': isOverlay,
        'bg-white': template !== 'page' && template !== 'overlay' && data.theme !== 'light-grey',
        'bg-light-grey':
          template === 'page' || (template !== 'overlay' && data.theme === 'light-grey'),
      },
      data.overlays?.length ? 'max-lg:h-[110vh]' : 'max-lg:h-screen',
    ]"
  >
    <div
      v-if="data.showTextAnimation"
      class="text-neon-yellow pointer-events-none absolute inset-0 z-1 grid place-items-center"
    >
      <div ref="$animation" class="w-[85vw] max-w-xl" />
    </div>

    <CommonMedia
      :sizes="'80vw'"
      v-bind="{ ...data, ...videoProps }"
      :fill="'both'"
      :class="[data.mediaMobile ? 'max-lg:hidden' : '']"
      :loading="sectionIndex === 0 ? 'eager' : 'lazy'"
      class="h-[110vh]"
    />

    <CommonMedia
      v-if="data.mediaMobile"
      :sizes="'80vw'"
      v-bind="{ media: data.mediaMobile, ...videoProps }"
      class="h-[110vh] lg:hidden"
      :loading="sectionIndex === 0 ? 'eager' : 'lazy'"
    />
    <div
      v-if="data.overlays"
      class="absolute right-0 bottom-0 z-2 ml-auto flex h-full items-end lg:w-1/2"
    >
      <div class="bottom-0 flex flex-col lg:sticky lg:ml-auto lg:w-1/2">
        <ElementsOverlayButton
          v-for="(overlay, index) in data.overlays"
          :key="index"
          :overlay="overlay"
          class="lg:w-full"
        />
      </div>
    </div>
  </section>
</template>
